// fallow-ignore-file code-duplication
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { T, useGT } from 'gt-react';
import type {
  ReportLineType,
  ReportTable as ReportTableType,
} from '@erp/shared-types';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTable } from '@/components/reports/ReportTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useSupplierYoY } from '@/api/rebates/useReports';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { ShareNetworkIcon, LinkIcon } from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadRebateMultiTableXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { RebateReportDownloadMenu } from '@/components/rebates/reports/export/RebateReportDownloadMenu';
import {
  rebateMultiTableDownloadFilename,
  rebateMultiTableFinalFilename,
} from '@/components/rebates/reports/export/rebateMultiTableDownloadFilename';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HeaderPageTitle } from 'src/context/header-title.context';

const REPORT_ID = 'supplier-yoy';

export interface SupplierYoYPageProps {
  lockedFilters?: {
    year: number;
    divisionIds?: string[];
    thisYearLineType?: ReportLineType;
    lastYearLineType?: ReportLineType;
    categoryFilters?: string[];
    showColumns?: string[];
  };
}

export function SupplierYoYPage({ lockedFilters }: SupplierYoYPageProps = {}) {
  const isShared = !!lockedFilters;
  const gt = useGT();
  const reportTitle = gt('Supplier YoY Comparison', {
    context: 'rebates report name for supplier year-over-year comparison',
  });
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    MultiSelectOption[]
  >([]);
  const hasSetDefaultDivision = useRef(false);
  const hasSetDefaultCategories = useRef(false);
  const [thisYearLineType, setThisYearLineType] =
    useState<ReportLineType>('predictions');
  const [lastYearLineType, setLastYearLineType] =
    useState<ReportLineType>('reported');
  const [throughMonth, setThroughMonth] = useState<number>(currentMonth);

  const monthNames = useMemo(
    () => [
      gt('January', { context: 'calendar month name' }),
      gt('February', { context: 'calendar month name' }),
      gt('March', { context: 'calendar month name' }),
      gt('April', { context: 'calendar month name' }),
      gt('May', { context: 'calendar month name' }),
      gt('June', { context: 'calendar month name' }),
      gt('July', { context: 'calendar month name' }),
      gt('August', { context: 'calendar month name' }),
      gt('September', { context: 'calendar month name' }),
      gt('October', { context: 'calendar month name' }),
      gt('November', { context: 'calendar month name' }),
      gt('December', { context: 'calendar month name' }),
    ],
    [gt],
  );
  const [showDeltaColumns, setShowDeltaColumns] = useState<
    MultiSelectOption[] | null
  >(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx'>('csv');

  const { data: divisionsData, isLoading: divisionsLoading } = useDivisions();

  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear)
      .sort((a, b) => b - a)
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  const lineTypeOptions: Array<{
    id: ReportLineType;
    label: string;
    value: ReportLineType;
  }> = useMemo(
    () => [
      {
        id: 'estimates',
        label: gt('Estimates', { context: 'rebate report line type option' }),
        value: 'estimates',
      },
      {
        id: 'reported',
        label: gt('Reported', { context: 'rebate report line type option' }),
        value: 'reported',
      },
      {
        id: 'predictions',
        label: gt('Predictions', { context: 'rebate report line type option' }),
        value: 'predictions',
      },
    ],
    [gt],
  );

  // Category options
  const categoryOptions: MultiSelectOption[] = useMemo(
    () => [
      {
        id: 'Redistribution',
        label: 'Redistribution',
        value: 'Redistribution',
      },
      { id: 'Conditional', label: 'Conditional', value: 'Conditional' },
      {
        id: 'Non-Conditional',
        label: 'Non-Conditional',
        value: 'Non-Conditional',
      },
    ],
    [],
  );

  // Column visibility options
  const deltaColumnOptions: MultiSelectOption[] = useMemo(
    () => [
      {
        id: 'thisYear',
        label: gt('This Year', {
          context: 'supplier YoY report column visibility option',
        }),
        value: 'thisYear',
      },
      {
        id: 'lastYear',
        label: gt('Last Year', {
          context: 'supplier YoY report column visibility option',
        }),
        value: 'lastYear',
      },
      {
        id: 'dollarDelta',
        label: gt('$ Delta', {
          context: 'supplier YoY report column visibility option',
        }),
        value: 'dollarDelta',
      },
      {
        id: 'percentDelta',
        label: gt('% Delta', {
          context: 'supplier YoY report column visibility option',
        }),
        value: 'percentDelta',
      },
    ],
    [gt],
  );

  useEffect(() => {
    if (showDeltaColumns === null && deltaColumnOptions.length > 0) {
      setShowDeltaColumns([...deltaColumnOptions]);
    }
  }, [showDeltaColumns, deltaColumnOptions]);

  // Generate default filename
  const defaultFilename = useMemo(() => {
    const divisionPart =
      selectedDivisions.length > 0 &&
      selectedDivisions.length < divisionOptions.length
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    return `supplier-yoy-${selectedYear}${divisionPart}`;
  }, [selectedYear, selectedDivisions, divisionOptions.length]);

  const handleDownloadClick = (format: 'csv' | 'xlsx') => {
    if (!report) return;
    setDownloadFormat(format);
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!filteredTable) return;

    const options = {
      tables: [
        {
          title: filteredTable.title || reportTitle,
          table: filteredTable,
          rowLabelHeader: gt('Supplier', {
            context: 'CSV header for supplier code column in YoY export',
          }),
        },
      ],
      filename: rebateMultiTableFinalFilename(filename, downloadFormat),
    };
    if (downloadFormat === 'xlsx') {
      void downloadRebateMultiTableXlsx(options);
      return;
    }
    downloadMultiTableCSV(options);
  };

  // Default to ALL divisions when divisions are loaded (only once)
  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
  }, [divisionOptions, selectedDivisions.length, divisionsLoading]);

  // Default to ALL categories (only once)
  useEffect(() => {
    if (
      categoryOptions.length > 0 &&
      selectedCategories.length === 0 &&
      !hasSetDefaultCategories.current
    ) {
      setSelectedCategories([...categoryOptions]);
      hasSetDefaultCategories.current = true;
    }
  }, [categoryOptions, selectedCategories.length]);

  const effectiveYear = lockedFilters?.year ?? selectedYear;
  const effectiveDivisionIds =
    lockedFilters?.divisionIds ??
    (selectedDivisions.length > 0
      ? selectedDivisions.map((d) => d.id)
      : undefined);
  const effectiveThisYearLineType =
    lockedFilters?.thisYearLineType ?? thisYearLineType;
  const effectiveLastYearLineType =
    lockedFilters?.lastYearLineType ?? lastYearLineType;
  const effectiveCategoryFilters =
    lockedFilters?.categoryFilters ??
    (selectedCategories.length > 0
      ? selectedCategories.map((c) => c.id)
      : undefined);

  const {
    data: report,
    isLoading,
    error,
  } = useSupplierYoY({
    year: effectiveYear,
    divisionIds: effectiveDivisionIds,
    thisYearLineType: effectiveThisYearLineType,
    lastYearLineType: effectiveLastYearLineType,
    categoryFilters: effectiveCategoryFilters,
    throughMonth: isShared ? undefined : throughMonth,
  });

  const lockedShowColumns = lockedFilters?.showColumns;
  const effectiveShowDeltaColumns = lockedShowColumns
    ? deltaColumnOptions.filter((c) => lockedShowColumns.includes(c.id))
    : (showDeltaColumns ?? deltaColumnOptions);

  // Filter table columns based on showDeltaColumns selection
  const filteredTable = useMemo((): ReportTableType | null => {
    if (!report?.table) return null;

    const showThisYear = effectiveShowDeltaColumns.some(
      (c) => c.id === 'thisYear',
    );
    const showLastYear = effectiveShowDeltaColumns.some(
      (c) => c.id === 'lastYear',
    );
    const showDollar = effectiveShowDeltaColumns.some(
      (c) => c.id === 'dollarDelta',
    );
    const showPercent = effectiveShowDeltaColumns.some(
      (c) => c.id === 'percentDelta',
    );

    // Column indices: 0=Supplier Code, 1=This Year, 2=Last Year, 3=$ Delta, 4=% Delta
    const visibleIndices = [0];
    if (showThisYear) visibleIndices.push(1);
    if (showLastYear) visibleIndices.push(2);
    if (showDollar) visibleIndices.push(3);
    if (showPercent) visibleIndices.push(4);

    return {
      ...report.table,
      columnHeaders: visibleIndices.map((i) => report.table.columnHeaders[i]),
      rows: report.table.rows.map((row) => ({
        ...row,
        cells: visibleIndices.map((i) => row.cells[i]),
      })),
    };
  }, [report?.table, effectiveShowDeltaColumns]);

  const reportContent = (
    <>
      {isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>{reportTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      )}

      {error && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              <T context="error loading supplier YoY report">
                Error loading report data. Please try again.
              </T>
            </p>
          </CardContent>
        </Card>
      )}

      {filteredTable && !isLoading && (
        <ReportTable
          table={filteredTable}
          timeFilter="all"
          onTimeFilterChange={() => {}}
          rowLabel="Supplier"
          showTotals={false}
          showTimeFilter={false}
          enableSorting={true}
        />
      )}
    </>
  );

  if (isShared) {
    return reportContent;
  }

  return (
    <DashboardLayout>
      <div className={'p-1 w-full min-w-0 max-w-full overflow-hidden'}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <HeaderPageTitle>{reportTitle}</HeaderPageTitle>
            <p className="text-sm text-muted-foreground">
              <T context="supplier year-over-year rebate report page subtitle">
                Compare supplier rebate totals year-over-year with configurable
                data types
              </T>
            </p>
          </div>
          <div className="flex gap-2">
            <RebateReportDownloadMenu
              disabled={!report || isLoading}
              onDownload={handleDownloadClick}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="open share menu for report">Share</T>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="create a shareable link for this report">
                    Create share link
                  </T>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  <T context="manage existing share links for this report">
                    Manage shared links
                  </T>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Card className="mb-2">
          <CardHeader>
            <CardTitle>
              <T context="report filter card title">Filters</T>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <SingleSelect
                label={gt('Year', { context: 'report filter label' })}
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder={gt('Select year...', {
                  context: 'year filter placeholder',
                })}
                searchPlaceholder={gt('Search years...', {
                  context: 'year filter search box',
                })}
                shouldAutoSort={false}
              />
              <MultiSelect
                label={gt('Divisions', { context: 'report filter label' })}
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder={gt('Select divisions...', {
                  context: 'division filter placeholder',
                })}
                searchPlaceholder={gt('Search divisions...', {
                  context: 'division filter search box',
                })}
                disabled={divisionsLoading}
              />
              <SingleSelect
                label={gt('This Year Data', {
                  context: 'supplier YoY report filter label',
                })}
                labelPosition="left"
                options={lineTypeOptions}
                value={
                  lineTypeOptions.find(
                    (opt) => opt.value === thisYearLineType,
                  ) || null
                }
                onChange={(option) => {
                  if (option) {
                    setThisYearLineType(option.value);
                  }
                }}
                placeholder={gt('Select data type...', {
                  context: 'line type filter placeholder',
                })}
                searchPlaceholder={gt('Search...', {
                  context: 'generic filter search placeholder',
                })}
                shouldAutoSort={false}
              />
              <SingleSelect
                label={gt('Last Year Data', {
                  context: 'supplier YoY report filter label',
                })}
                labelPosition="left"
                options={lineTypeOptions}
                value={
                  lineTypeOptions.find(
                    (opt) => opt.value === lastYearLineType,
                  ) || null
                }
                onChange={(option) => {
                  if (option) {
                    setLastYearLineType(option.value);
                  }
                }}
                placeholder={gt('Select data type...', {
                  context: 'line type filter placeholder',
                })}
                searchPlaceholder={gt('Search...', {
                  context: 'generic filter search placeholder',
                })}
                shouldAutoSort={false}
              />
              <MultiSelect
                label={gt('Categories', { context: 'report filter label' })}
                labelPosition="left"
                options={categoryOptions}
                value={selectedCategories}
                onChange={setSelectedCategories}
                placeholder={gt('Select categories...', {
                  context: 'category filter placeholder',
                })}
                searchPlaceholder={gt('Search categories...', {
                  context: 'category filter search box',
                })}
              />
              <MultiSelect
                label={gt('Show Columns', {
                  context: 'supplier YoY visible columns filter',
                })}
                labelPosition="left"
                options={deltaColumnOptions}
                value={showDeltaColumns ?? []}
                onChange={setShowDeltaColumns}
                placeholder={gt('Select columns...', {
                  context: 'column visibility filter placeholder',
                })}
                searchPlaceholder={gt('Search columns...', {
                  context: 'column filter search box',
                })}
              />
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="through-month"
                  className="text-sm text-foreground whitespace-nowrap"
                >
                  <T context="label for month-through cutoff selector on rebate report">
                    Through:
                  </T>
                </Label>
                <Select
                  value={throughMonth.toString()}
                  onValueChange={(v) => setThroughMonth(Number(v))}
                >
                  <SelectTrigger
                    id="through-month"
                    className="w-[130px] h-8 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthNames.map((name, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {reportContent}

        <ShareReportModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          reportId={REPORT_ID}
          reportTitle={reportTitle}
          initialYear={selectedYear}
          initialDivisions={selectedDivisions}
          customFilters={[
            {
              key: 'thisYearLineType',
              label: gt('This Year Data', {
                context: 'share report modal filter label',
              }),
              options: lineTypeOptions.map((o) => ({
                id: o.id,
                label: o.label,
              })),
              initialValue: [thisYearLineType],
              placeholder: gt('Select data type...', {
                context: 'share modal line type placeholder',
              }),
              receiverDisplayType: 'single',
            },
            {
              key: 'lastYearLineType',
              label: gt('Last Year Data', {
                context: 'share report modal filter label',
              }),
              options: lineTypeOptions.map((o) => ({
                id: o.id,
                label: o.label,
              })),
              initialValue: [lastYearLineType],
              placeholder: gt('Select data type...', {
                context: 'share modal line type placeholder',
              }),
              receiverDisplayType: 'single',
            },
            {
              key: 'categoryFilters',
              label: gt('Categories', {
                context: 'share report modal filter label',
              }),
              options: categoryOptions.map((c) => ({
                id: c.id,
                label: c.label,
              })),
              initialValue: selectedCategories.map((c) => c.id),
              placeholder: gt('Select categories...', {
                context: 'share modal categories placeholder',
              }),
            },
            {
              key: 'showColumns',
              label: gt('Show Columns', {
                context: 'share report modal filter label',
              }),
              options: deltaColumnOptions.map((c) => ({
                id: c.id,
                label: c.label,
              })),
              initialValue: effectiveShowDeltaColumns.map((c) => c.id),
              placeholder: gt('Select columns...', {
                context: 'share modal column visibility placeholder',
              }),
            },
          ]}
        />
        <SharedLinksManager
          open={linksManagerOpen}
          onClose={() => setLinksManagerOpen(false)}
          reportId={REPORT_ID}
          reportTitle={reportTitle}
          onCreateNew={() => {
            setLinksManagerOpen(false);
            setShareModalOpen(true);
          }}
        />
        <DownloadModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          onDownload={handleDownload}
          defaultFilename={rebateMultiTableDownloadFilename(
            defaultFilename,
            downloadFormat,
          )}
          title={
            downloadFormat === 'xlsx'
              ? gt('Download {reportTitle} (.xlsx)', {
                  reportTitle,
                  context: 'download modal title for rebate report as Excel',
                })
              : gt('Download {reportTitle} (.csv)', {
                  reportTitle,
                  context: 'download modal title for rebate report as CSV',
                })
          }
          confirmLabel={
            downloadFormat === 'xlsx'
              ? gt('Download Excel', {
                  context: 'download Excel report button',
                })
              : gt('Download CSV', {
                  context: 'download report button',
                })
          }
        />
      </div>
    </DashboardLayout>
  );
}

export default SupplierYoYPage;
